import React from 'react';
import useReactRouter from 'use-react-router';
import MessageTemplatesApi from '~/App/Api/MessageTemplates';
import { useMutation, useQueryClient } from 'react-query';
import { unflatten } from '~/utils/Immutable';
import { EditorContextProps } from '../Context';
import Text from '~/App/Components/Text';
import { alertAsync } from '~/App/Components/Modals/Alert';

interface UpdateMessageTemplateError {
  response: {
    data: {
      error: {
        message: string;
      };
    };
  };
}

const useSaveMessageTemplate = (
  context: EditorContextProps
): [boolean, typeof updateMessageTemplate] => {
  const { state: attributes, setApiState, setHasUpdated } = context;
  const { match } = useReactRouter<{ id: string }>();
  const queryClient = useQueryClient();

  const { mutateAsync: updateMessageTemplate, isLoading: isSaving } =
    useMutation(
      () => {
        const id = match.params.id;
        const unFlattened = unflatten(attributes?.toJS());
        return MessageTemplatesApi.put(id, unFlattened);
      },
      {
        onSuccess: (updatedTemplate) => {
          queryClient.invalidateQueries('message-templates');
          queryClient.invalidateQueries('results');
          setHasUpdated(true);
          setApiState?.(updatedTemplate.data);
        },
        onError: async (error: UpdateMessageTemplateError) => {
          return !(await alertAsync(
            <>
              <Text.SubHeading>
                There was an error saving your message template.
              </Text.SubHeading>
              <Text.BodyCopy>{error.response.data.error.message}</Text.BodyCopy>
            </>
          ));
        },
      }
    );

  return [isSaving, updateMessageTemplate];
};

export default useSaveMessageTemplate;
